import httpService from '../../../../services/httpService';

export interface AssessmentListResponse {
    records: AssessmentEntity[];
    total_no_of_records: number;
    page_number: number;
    limit: number;
}

export interface AssessmentListParams {
    page_number?: number;
    limit?: number;
    status?: string;
    assessment_name?: string;
    start_date?: string;
    end_date?: string;
    offset_time?: string;
}

const getOffsetTime = () => {
    const offset = new Date().getTimezoneOffset();
    const sign = offset > 0 ? "-" : "+";
    const abs = Math.abs(offset);
    return `${sign}${String(Math.floor(abs / 60)).padStart(2, "0")}:${String(abs % 60).padStart(2, "0")}`;
};

export interface ProgrammingConfig {
    module_id: string;
    domain_type: string;
    question_count: number;
    difficulty_distribution: Record<string, number>;
    programming_languages?: string[];
}

export interface IndustryConfig {
    module_id: string;
    question_count: number;
    difficulty_distribution: Record<string, number>;
    scoring_scheme: Record<string, number>;
    show_answers?: boolean;
}

export interface AssessmentEntity {
    assessment_id?: string;
    assessment_name: string;
    assessment_duration: number;
    assessment_otp?: string | number;
    target_candidates?: string[];
    dimension_questions: boolean;
    number_of_dimension_questions: number;
    dimension_descriptive_questions?: number;
    dimension_list: string[];
    assessment_start_time: string;
    assessment_end_time: string;
    status?: string;
    created_at?: string;
    updated_at?: string;
    created_by?: string;
    company_id?: string;
    programming_configs?: ProgrammingConfig[];
    industry_configs?: IndustryConfig[];
    timezone?: string;
}

export interface AssessmentSkillScore {
    skill_id?: string;
    skill_name?: string;
    score?: number;
    correct?: number;
    total?: number;
}

export interface AssessmentSkillReport {
    dimension_id?: string;
    dimension_name?: string;
    dimension_score?: number;
    skill_score?: AssessmentSkillScore[];
}
export interface AssessmentResultRow {
    candidate_id: string;
    email_address?: string;
    first_name?: string;
    last_name?: string;
    attended?: boolean;
    status?: string;
    score?: number | string | null;
    jozuna_score?: number | string | null;
    programming_score?: number | string | null;
    industry_score?: number | string | null;
    correct_answers?: number | null;
    total_questions?: number | null;
    attempted_questions?: number | null;
    submitted_at?: string;
    skill_report?: AssessmentSkillReport[];
}

export interface AssessmentResultsResponse {
    assessment_entity_id: string;
    assessment_entity_name?: string;
    records: AssessmentResultRow[];
    total_no_of_records: number;
}

export interface DetailedProgrammingQuestion {
    question_id: string;
    problem_statement: string;
    programming_language: string;
    submitted_code: string;
    analysis?: any;
    is_correct?: boolean;
}

export interface DetailedIndustryQuestion {
    question_id: string;
    module_id: string;
    question: string;
    options?: string[];
    correct_answer?: string;
    selected_answer?: string;
    is_correct?: boolean;
}

export interface CandidateDetailedResult {
    candidate_id: string;
    assessment_id: string;
    overall_score?: number | string;
    skill_report: AssessmentSkillReport[];
    programming_report: {
        score?: number | string;
        questions: DetailedProgrammingQuestion[];
    };
    industry_report: {
        score?: number | string;
        questions: DetailedIndustryQuestion[];
    };
}

class AssessmentClient {

    async createAssessment(data: AssessmentEntity): Promise<{ message: string }> {
        const response = await httpService.post<{ message: string }>('/assessment/entities', data);
        return response.data;
    }

    async getAssessments(params?: AssessmentListParams): Promise<AssessmentListResponse> {
        const queryParams = new URLSearchParams();
        if (params?.page_number) queryParams.append('page_number', String(params.page_number));
        if (params?.limit) queryParams.append('limit', String(params.limit));
        if (params?.status) queryParams.append('status', params.status);
        if (params?.assessment_name) queryParams.append('assessment_name', params.assessment_name);
        if (params?.start_date) queryParams.append('start_date', params.start_date);
        if (params?.end_date) queryParams.append('end_date', params.end_date);
        queryParams.append('offset_time', getOffsetTime());
        const qs = queryParams.toString();
        const response = await httpService.get<AssessmentListResponse>(`/assessment/entities${qs ? '?' + qs : ''}`);
        return response.data;
    }

    async getAssessmentById(id: string): Promise<AssessmentEntity> {
        const response = await httpService.get<AssessmentEntity>(`/assessment/entities/${id}`);
        return response.data;
    }

    async submitCandidateAnswer(candidateId: string, assessmentId: string, data: any): Promise<any> {
        const response = await httpService.post<any>(`/candidate/${candidateId}/assessment/${assessmentId}/answer`, data);
        return response.data;
    }

    async getCandidateDetailedResults(assessmentId: string, candidateId: string): Promise<CandidateDetailedResult> {
        const response = await httpService.get<CandidateDetailedResult>(`/assessment/candidate/result?assessment_id=${assessmentId}&candidate_id=${candidateId}`);
        return response.data;
    }

    async updateAssessment(id: string, data: Partial<AssessmentEntity>): Promise<AssessmentEntity> {
        const response = await httpService.post<AssessmentEntity>(`/assessment/entities/${id}`, data);
        return response.data;
    }

    async deleteAssessment(id: string): Promise<void> {
        await httpService.delete(`/assessment/entities/${id}`);
    }

    async closeAssessment(id: string): Promise<void> {
        await httpService.post(`/assessment/close?assessment_id=${id}`, {});
    }

    async getAssessmentResults(id: string): Promise<AssessmentResultsResponse> {
        const response = await httpService.get<AssessmentResultsResponse>(`/assessment/entities/${id}/results`);
        return response.data;
    }

    async getDomains(type?: string): Promise<any[]> {
        const queryParams = new URLSearchParams();
        if (type) queryParams.append('type', type);
        const response = await httpService.get(`/assessment/domains?${queryParams.toString()}`);
        return response.data;
    }

    async getModuleStats(domainType: 'programming' | 'industry', moduleId: string): Promise<{ total: number, easy: number, medium: number, hard: number }> {
        const response = await httpService.post<{ total: number, easy: number, medium: number, hard: number }>('/assessment/module-stats', {
            domain_type: domainType,
            module_id: moduleId
        });
        return response.data;
    }
}

export default new AssessmentClient();
